import { useEffect, useState } from "react";
import { api, ApiError } from "@/api/client";
import { useStore } from "@/state/store";
import { IconSave } from "@/components/SvoIcons";
import type { RequirementFields, Topic } from "@offsite/shared";

const FIELDS: { key: keyof RequirementFields; label: string }[] = [
  { key: "was", label: "Was soll gebaut werden?" },
  { key: "fuerWen", label: "Für wen ist das?" },
  { key: "problem", label: "Welches Problem löst es?" },
  { key: "anforderungen", label: "Was muss es können?" },
  { key: "einschraenkungen", label: "Was darf es NICHT?" },
  { key: "beispiel", label: "Beispiel-Szenario" },
];

const EMPTY: RequirementFields = {
  was: "",
  fuerWen: "",
  problem: "",
  anforderungen: "",
  einschraenkungen: "",
  beispiel: "",
};

export function AdminRequirementsSection() {
  const { state } = useStore();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [activeTopic, setActiveTopic] = useState<string | null>(null);
  const [fields, setFields] = useState<RequirementFields>(EMPTY);
  const [status, setStatus] = useState<"draft" | "submitted" | "missing">("missing");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const r = await api.topics.list(state.auth.token);
      setTopics(r.topics);
      if (r.topics.length > 0 && !activeTopic) setActiveTopic(r.topics[0].id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.auth.token]);

  useEffect(() => {
    if (!activeTopic) return;
    let cancelled = false;
    (async () => {
      setMsg(null);
      try {
        const r = await api.requirements.get(state.auth.token, activeTopic);
        if (cancelled) return;
        setFields(r.fields);
        setStatus(r.status);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setFields(EMPTY);
          setStatus("missing");
        } else {
          console.error(err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTopic, state.auth.token]);

  const update = (key: keyof RequirementFields, v: string) => {
    setFields((f) => ({ ...f, [key]: v }));
    setMsg(null);
  };

  const save = async () => {
    if (!activeTopic) return;
    setBusy(true);
    try {
      await api.admin.putRequirement(state.auth.token, activeTopic, fields);
      setMsg("Anforderung gespeichert (Admin-Override).");
    } catch (err) {
      console.error(err);
      setMsg("Konnte nicht speichern.");
    } finally {
      setBusy(false);
    }
  };

  const statusKlass =
    status === "submitted"
      ? "status-pill is-good"
      : status === "draft"
        ? "status-pill is-info"
        : "status-pill";
  const statusLabel =
    status === "submitted" ? "eingereicht" : status === "draft" ? "Entwurf" : "leer";

  return (
    <div className="split-2pane">
      <aside className="pane-list">
        <h2
          style={{
            margin: 0,
            color: "var(--svo-dunkelblau)",
            fontSize: 16,
            fontWeight: 600,
          }}
        >
          Themen
        </h2>
        {topics.length === 0 && (
          <p className="form-hint">Keine Themen — erst über „Themen"-Tab anlegen.</p>
        )}
        {topics.map((t, i) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActiveTopic(t.id)}
            className={`topic-pick${activeTopic === t.id ? " is-active" : ""}`}
          >
            <span className="badge">{i + 1}</span>
            <span style={{ flex: 1 }}>{t.title}</span>
          </button>
        ))}
      </aside>

      <section className="pane-detail">
        <header
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <h2
            style={{
              margin: 0,
              color: "var(--svo-dunkelblau)",
              fontSize: 22,
              fontWeight: 500,
            }}
          >
            Anforderung bearbeiten
          </h2>
          {activeTopic && (
            <span className={statusKlass}>
              <span className="dot" />
              {statusLabel}
            </span>
          )}
        </header>

        {!activeTopic && <p className="form-hint">Wähle ein Thema links.</p>}

        {activeTopic &&
          FIELDS.map(({ key, label }) => (
            <label key={key} className="req-field">
              <span className="req-field-label">{label}</span>
              <textarea
                className="input-textarea"
                value={fields[key]}
                onChange={(e) => update(key, e.target.value)}
                rows={key === "was" || key === "beispiel" ? 3 : 2}
              />
            </label>
          ))}

        {activeTopic && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
              alignItems: "center",
            }}
          >
            {msg && (
              <span style={{ color: "var(--svo-gruen)", fontWeight: 600 }}>{msg}</span>
            )}
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="btn btn-primary"
            >
              <IconSave size={16} />
              {busy ? "…" : "Speichern"}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
